/**
 * PLAT-12088 — the Set as Graphic Standard modal shows and edits a font
 * size in px, the unit the rest of the editor speaks, while the standard
 * itself stays stored in the viewport unit the template uses ("N vh" from
 * the side panel, "N vmin" from older templates).
 *
 * Conversions use a 1280 × 720 reference canvas, so "4.7222 vh" reads as
 * 34px here exactly as it does in the toolbar's stepper. vh is right for
 * every aspect (all are 720 tall); vw/vmin/vmax assume 16:9 widths and read
 * high on 9:16 and 1:1. The app only writes vh, so those come only from
 * templates made outside it.
 */
const REFERENCE = { width: 1280, height: 720 };

const LENGTH = /^\s*(-?\d*\.?\d+)\s*([a-z%]*)\s*$/i;

// px per 100 units of a viewport unit; null for px itself (and a bare
// number); undefined for a unit this helper does not know.
const basisFor = (unit: string): number | null | undefined => {
  switch (unit) {
    case 'vh':
      return REFERENCE.height;
    case 'vw':
      return REFERENCE.width;
    case 'vmin':
      return Math.min(REFERENCE.width, REFERENCE.height);
    case 'vmax':
      return Math.max(REFERENCE.width, REFERENCE.height);
    case 'px':
    case '':
      return null;
    default:
      return undefined;
  }
};

/** The unit a stored font size carries; `vh` when it has none yet. */
export const fontSizeUnit = (value: unknown): string => {
  const m = LENGTH.exec(String(value ?? ''));
  return m?.[2] || 'vh';
};

/**
 * A stored font size ("4.7222 vh", "22.87 vmin", "33px", 33) in px on the
 * reference canvas, or null for an empty or unreadable value.
 */
export const fontSizeToPx = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return value;
  const m = LENGTH.exec(String(value));
  if (!m) return null;
  const amount = Number(m[1]);
  const basis = basisFor(m[2]);
  if (basis === undefined) return null;
  return basis === null ? amount : (amount * basis) / 100;
};

/** Whole px for display: "34px", or "Auto" when the text sizes itself. */
export const formatFontSizePx = (value: unknown): string => {
  const px = fontSizeToPx(value);
  return px == null ? 'Auto' : `${Math.round(px)}px`;
};

/**
 * px back into the unit the standard is stored in, four decimals like the
 * values the templates carry ("4.6787 vh"). An unknown unit falls back to vh.
 */
export const pxToFontSize = (px: number, unit: string): string => {
  const basis = basisFor(unit);
  if (basis === null) return `${px}px`;
  const amount = Number(((px * 100) / (basis ?? REFERENCE.height)).toFixed(4));
  return `${amount} ${basis === undefined ? 'vh' : unit}`;
};
